import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useRepository, useUpdateRepository } from '../contexts/repositories';
import { toUpdatePayload, validate, valuesFrom } from '../utils/form';
import RepositoryEnvironments from '../layouts/environments';
import RepositoryStandards from '../layouts/standards';
import RepositoryFields from './fields';

import '../../clients/styles/form.css';

export default function EditRepositoryForm({ repositoryId }) {
    const { data, isLoading, error } = useRepository(repositoryId);

    if (isLoading) return <p className="client-form__loading">Cargando repositorio...</p>;

    if (error) {
        return (
            <p className="client-form__failure">
                {error.message || 'No se pudo cargar el repositorio'}
            </p>
        );
    }

    const repository = data?.repository ?? data;
    return <RepositoryEditor key={repository.id} repository={repository} />;
}

function RepositoryEditor({ repository }) {
    const updateRepository = useUpdateRepository(repository.id);

    const [values, setValues] = useState(() => valuesFrom(repository));
    const [errors, setErrors] = useState({});

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validate(values, { withProject: false });
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            await updateRepository.mutateAsync(toUpdatePayload(values));
            toast.success('Repositorio guardado');
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo guardar el repositorio');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">{repository.name}</h2>
                <p className="client-form__subtitle">
                    Repositorio #{repository.id}
                    {repository.clone_protocol && <> · clona por {repository.clone_protocol}</>}
                    {/* Written by the delivery, never by this form. */}
                    {repository.last_commit && (
                        <> · último commit <code>{String(repository.last_commit).slice(0, 7)}</code></>
                    )}
                </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos del repositorio</h3>
                        <p className="client-form__section-text">
                            El último commit lo escribe la entrega, no este formulario.
                        </p>
                    </div>

                    <RepositoryFields
                        projectLabel={repository.project?.name}
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={updateRepository.isPending}
                        projectLocked
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/projects?tab=repositorios" className="client-form__cancel">Volver</Link>
                    <button
                        type="submit"
                        className="client-form__submit"
                        disabled={updateRepository.isPending}
                    >
                        {updateRepository.isPending ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>
            </form>

            <div className="client-form__card client-form__card--stacked">
                <RepositoryEnvironments repositoryId={repository.id} />
            </div>

            <div className="client-form__card client-form__card--stacked">
                <RepositoryStandards repository={repository} />
            </div>
        </section>
    );
}
